//! API calls
// API calls to backend
import { INTERVIEW_API_BASE_URL, getAccessToken, getAuthHeaders } from '../../config/api';
const API_BASE_URL = INTERVIEW_API_BASE_URL;

const normalizeQuestionItems = (data) => {
    const items = Array.isArray(data?.question_items) ? data.question_items : [];
    if (items.length) {
        return items.map((item, idx) => ({
            id: item?.id ?? idx + 1,
            question: String(item?.question ?? item ?? ''),
            audio_url: item?.audio_url || item?.audioUrl || '',
            audio_status: item?.audio_status || '',
            audio_provider: item?.audio_provider || 'elevenlabs',
            audio_cache_key: item?.audio_cache_key || '',
        }));
    }

    return (Array.isArray(data?.questions) ? data.questions : []).map((q, idx) => ({
        id: idx + 1,
        question: String(q),
        audio_url: '',
        audio_status: 'missing',
        audio_provider: 'elevenlabs',
    }));
};

export const generateQuestions = async (jobRole, sessionConfig, difficulty) => {
    if (!jobRole.trim()) {
        throw new Error('Please enter a job role');
    }
    if (!getAccessToken()) {
        throw new Error('Login required to generate AI questions');
    }

    const interviewType = sessionConfig?.selectedType || 'behavioral';
    const industry = sessionConfig?.selectedIndustry || 'technology';
    const questionCount = Math.min(sessionConfig?.estimatedQuestions || 5, 10);
    const personaStyle = sessionConfig?.personality?.selectedPersona?.description || 'professional';
    // Read voice_id directly from the personality object (set by AIPersonalityCustomizer)
    const voiceId = sessionConfig?.personality?.elevenlabsVoice || null;

    console.log('📡 Sending to backend to generate questions:', {
        jobRole,
        interviewType,
        industry,
        questionCount,
        difficulty,
        personaStyle,
        voiceId,
    });

    const response = await fetch(`${API_BASE_URL}/generate-questions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            interviewId: localStorage.getItem('current_interview_id') || null,
            questionCount: questionCount,
            interviewType: interviewType,
            jobRole: jobRole,
            industry: industry,
            difficulty: difficulty,
            personaStyle: personaStyle,
            tone: sessionConfig?.personality?.tone || 3,
            difficulty_level: sessionConfig?.personality?.difficulty || 3,
            starMethodEnforcement: sessionConfig?.starMethodEnforcement || false,
            enableFollowUps: sessionConfig?.enableFollowUps || false,
            voiceId: voiceId,
        })
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    return normalizeQuestionItems(data);
};

//! analyze
export const analyzeTextWithBackend = async (text, currentQuestion, answerStartTimeRef, delaySeconds = 2) => {
    if (!text.trim()) return null;
    if (!getAccessToken()) return null;

    try {
        const duration = (Date.now() - (answerStartTimeRef?.current || Date.now())) / 1000;
        const delay = typeof delaySeconds === 'number' ? delaySeconds : 2;

        console.log('📡 Sending to backend for analysis:', { textLength: text.length, duration, delay });

        const response = await fetch(`${API_BASE_URL}/analyze`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                text: text,
                question: currentQuestion,
                duration: duration,
                delay: delay,
                use_ai: true
            })
        });

        if (!response.ok) {
            throw new Error(`Backend analysis failed: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ Backend analysis response:', data);

        return data;
    } catch (error) {
        console.error('❌ Backend analysis error:', error);
        return null;
    }
};

//! final-analysis
export const analyzeAnswersWithAI = async (answers, fillerWordCounts, metrics) => {
    if (!getAccessToken()) {
        throw new Error('Login required to analyze interview');
    }
    // Prepare answers for backend
    const answersForBackend = answers.map(answer => ({
        question: answer.question,
        transcript: answer.transcript || '',
        duration: answer.duration || 30,
        metrics: {
            clarity: answer.metrics.clarity || 0,
            pacing: answer.metrics.pacing || 0,
            response_time: answer.metrics.responseTime || 85,
            professionalism: answer.metrics.professionalism || 0,
            tone: answer.metrics.tone || 0
        }
    }));

    // Send to backend for comprehensive analysis
    const response = await fetch(`${API_BASE_URL}/final-analysis`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            answers: answersForBackend,
            fillerWords: fillerWordCounts,
            metrics: metrics,
            use_ai: true
        })
    });

    if (!response.ok) {
        throw new Error('Backend analysis failed');
    }

    return await response.json();
};


//! submit-results (prepared like final-analysis)
export const submitInterviewResultsPrepared = async ({
    interviewId,
    finalRes,         // response from /api/interview/final-analysis
    answersList = [], // your answersList used in runFinalAnalysis
    fillerWords = {}, // fillerWordsAgg (object)
}) => {
    if (!getAccessToken()) {
        throw new Error("Login required to submit interview results");
    }
    if (!interviewId) {
        throw new Error("Missing interviewId");
    }

    // ---------------- helpers ----------------
    const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
    const sumObj = (obj) =>
        Object.values(obj || {}).reduce((s, v) => s + (Number(v) || 0), 0);

    // Duration in your app can be seconds or ms → normalize best-effort
    const toSeconds = (d) => {
        const n = Number(d) || 0;
        return n > 1000 ? n / 1000 : n;
    };

    const wordCount = (t) =>
        String(t || "")
            .trim()
            .split(/\s+/)
            .filter(Boolean).length;

    const pickFinalScore = () => {
        const s =
            typeof finalRes?.final_score === "number"
                ? finalRes.final_score
                : typeof finalRes?.local_analysis?.final_score === "number"
                    ? finalRes.local_analysis.final_score
                    : typeof finalRes?.local_analysis?.final_score === "number"
                        ? finalRes.local_analysis.final_score
                        : typeof finalRes?.local_analysis?.final_score === "number"
                            ? finalRes.local_analysis.final_score
                            : typeof finalRes?.final_score === "number"
                                ? finalRes.final_score
                                : typeof finalRes?.final_score === "number"
                                    ? finalRes.final_score
                                    : typeof finalRes?.final_score === "number"
                                        ? finalRes.final_score
                                        : typeof finalRes?.final_score === "number"
                                            ? finalRes.final_score
                                            : typeof finalRes?.final_score === "number"
                                                ? finalRes.final_score
                                                : typeof finalRes?.final_score === "number"
                                                    ? finalRes.final_score
                                                    : typeof finalRes?.final_score === "number"
                                                        ? finalRes.final_score
                                                        : typeof finalRes?.final_score === "number"
                                                            ? finalRes.final_score
                                                            : typeof finalRes?.final_score === "number"
                                                                ? finalRes.final_score
                                                                : typeof finalRes?.final_score === "number"
                                                                    ? finalRes.final_score
                                                                    : typeof finalRes?.final_score === "number"
                                                                        ? finalRes.final_score
                                                                        : typeof finalRes?.final_score === "number"
                                                                            ? finalRes.final_score
                                                                            : typeof finalRes?.final_score === "number"
                                                                                ? finalRes.final_score
                                                                                : typeof finalRes?.final_score === "number"
                                                                                    ? finalRes.final_score
                                                                                    : typeof finalRes?.final_score === "number"
                                                                                        ? finalRes.final_score
                                                                                        : typeof finalRes?.final_score === "number"
                                                                                            ? finalRes.final_score
                                                                                            : 0;

        return clamp(Math.round(Number(s) || 0), 0, 100);
    };

    // ---------------- derive analytics ----------------
    const totalSeconds = (answersList || []).reduce(
        (acc, a) => acc + toSeconds(a?.duration ?? 0),
        0
    );
    const totalWords = (answersList || []).reduce(
        (acc, a) => acc + wordCount(a?.transcript),
        0
    );
    const minutes = totalSeconds > 0 ? totalSeconds / 60 : 0;
    const speakingPaceWpm = minutes > 0 ? Math.round(totalWords / minutes) : 0;

    const fillerWordCount = sumObj(fillerWords);
    const totalQuestionsAsked = (answersList || []).length;

    // ---------------- map finalRes -> backend payload ----------------
    const overallScore = pickFinalScore();

    // Pull averaged metrics (backend final-analysis computes these into statistics.average_metrics)
    const avgMetrics =
        finalRes?.local_analysis?.statistics?.average_metrics ||
        finalRes?.local_analysis?.statistics?.average_metrics ||
        {};

    // Performance categories must be an object (schema)
    // Keep it simple + truthful: store what you actually have.
    const performanceCategories = {
        communication: {
            score: Number(avgMetrics?.clarity ?? 0),
            max_score: 100,
            description: "Clarity / articulation (aggregated)",
        },
        professionalism: {
            score: Number(avgMetrics?.professionalism ?? 0),
            max_score: 100,
            description: "Professional tone / vocabulary (aggregated)",
        },
        confidence: {
            score: Number(avgMetrics?.tone ?? 0),
            max_score: 100,
            description: "Confidence / tone (aggregated)",
        },
        pacing: {
            score: Number(avgMetrics?.pacing ?? 0),
            max_score: 100,
            description: "Speaking pace (aggregated)",
        },
        response_time: {
            score: Number(avgMetrics?.response_time ?? 0),
            max_score: 100,
            description: "Response latency (aggregated)",
        },
    };

    // Strengths/weakest_areas must be arrays (schema).
    // Convert insight objects to strings to keep frontend stable.
    const localInsights = finalRes?.local_analysis?.insights || {};
    const strengths = Array.isArray(localInsights?.strengths)
        ? localInsights.strengths
            .map((x) => x?.description || x?.category || "")
            .filter(Boolean)
        : [];

    const weakestAreas = Array.isArray(localInsights?.areas_for_improvement)
        ? localInsights.areas_for_improvement
            .map((x) => x?.issue || x?.category || "")
            .filter(Boolean)
        : [];

    // detailed_feedback must be an array (schema). Keep minimal but valid.
    const detailedFeedback = Array.isArray(localInsights?.recommendations)
        ? [
            {
                id: "summary",
                title: "Summary",
                icon: "ClipboardList",
                score: overallScore,
                insights: (localInsights?.recommendations || []).slice(0, 6),
            },
        ]
        : [];

    // answer_reviews must be an array (schema).
    // Build from your answersList (matches InterviewSession comment structure). :contentReference[oaicite:3]{index=3}
    const answerReviews = (answersList || []).map((a, idx) => {
        const perAnswerScore =
            typeof a?.analysis?.final_score === "number"
                ? a.analysis.final_score
                : typeof a?.analysis?.local_analysis?.overall_score === "number"
                    ? a.analysis.local_analysis.overall_score
                    : (() => {
                        const c = Number(a?.metrics?.clarity ?? 0);
                        const p = Number(a?.metrics?.pacing ?? 0);
                        const conf = Number(a?.metrics?.confidence ?? 0);
                        return clamp(Math.round((c + p + conf) / 3), 0, 100);
                    })();

        const highlights = {
            strengths:
                a?.analysis?.local_analysis?.insights?.strengths?.map((s) => s?.description).filter(Boolean) || [],
            improvements:
                a?.analysis?.local_analysis?.insights?.areas_for_improvement?.map((i) => i?.issue).filter(Boolean) || [],
        };

        return {
            id: idx + 1,
            question: a?.question || "",
            user_answer: a?.transcript || "",
            ai_model_answer: a?.analysis?.ai_model_answer || "",
            score: clamp(Math.round(Number(perAnswerScore) || 0), 0, 100),
            highlights,
            audio_url: a?.audio_url || "",
            duration: a?.duration ?? 0,
        };
    });

    // improvement_suggestions must be an array (schema).
    const improvementSuggestions = Array.isArray(localInsights?.recommendations)
        ? localInsights.recommendations.map((r, idx) => ({
            id: idx + 1,
            category: r?.category || "General",
            priority: r?.priority || "medium",
            title: r?.action || "Improve",
            description: r?.expected_improvement || "",
            action_items: r?.specific_steps || [],
            resources: [],
            estimated_time: "",
            difficulty: "",
        }))
        : [];

    // next_steps must be an object (schema).
    const nextSteps = {
        recommended_practice: [],
        milestones: [],
        next_milestone: null,
        priority_actions: Array.isArray(localInsights?.priority_actions)
            ? localInsights.priority_actions
            : [],
    };

    // feedback must be string (schema).
    const feedback =
        finalRes?.ai_analysis?.overall_assessment ||
        localInsights?.overall_assessment ||
        "";

    // star_method_usage_count must be number if provided (schema) → keep safe.
    const starMethodUsageCount =
        typeof finalRes?.ai_analysis?.content_analysis?.star_method_usage_count === "number"
            ? finalRes.ai_analysis.content_analysis.star_method_usage_count
            : 0;

    // ---------------- final payload (matches validate_results_submission_payload) ----------------
    const resultsData = {
        overall_score: overallScore,
        performance_score: overallScore,
        feedback,

        performance_categories: performanceCategories,
        strengths,
        weakest_areas: weakestAreas,

        detailed_feedback: detailedFeedback,
        answer_reviews: answerReviews,
        improvement_suggestions: improvementSuggestions,
        next_steps: nextSteps,

        speaking_pace_wpm: speakingPaceWpm,
        filler_word_count: fillerWordCount,
        star_method_usage_count: starMethodUsageCount,
        total_questions_asked: totalQuestionsAsked,
        // eye_contact_percentage: omit for phone (if you send it, it must be number/null)
    };

    // ---------------- send to backend ----------------
    const res = await fetch(
        `${API_BASE_URL}/${interviewId}/results`,
        {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(resultsData),
            credentials: "include",
        }
    );

    if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(
            errBody?.error || "Failed to submit interview results"
        );
    }

    return await res.json();
};